import { css } from '@emotion/core';
import {
    Avatar,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Tab,
    Tabs,
    Theme,
    Tooltip,
    Typography,
    useTheme
} from '@material-ui/core';
import { BugReport, CheckCircleOutline } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import ReactJsonView from '@microlink/react-json-view';
import React from 'react';

import { downloadFile } from '../../api';
import { sanitizeHtmlForIframe } from '../../common/linkedin-data';
import { notificationTime } from '../../common/timestamp';
import { useSlides } from '../../hooks/use-candidate-slides';
import { useModal } from '../../hooks/use-modal';
import { usePromptInfo } from '../../hooks/use-prompt-info';
import { useSession } from '../../hooks/use-session';
import { PersonProfile } from '../../sfc/person-profile';
import { RenderRawHTMLSafely } from '../render-raw-html-safely';
import { notificationStyles } from './notification-styles';

const styles = (theme: Theme) => css`
    ${notificationStyles(theme)}

    .item-actions {
        display: flex;
        position: absolute;
        bottom: 10px;
        right: 12px;
        opacity: 0;

        .MuiIconButton-root {
            margin-left: 5px;
        }
    }

    &:hover {
        background: rgba(0, 0, 0, 0.025);
        .item-actions {
            opacity: 1;
        }
    }
`;

const dialogStyles = css`
    .MuiDialogContent-root {
        padding-top: 0;

        .MuiList-root {
            margin-bottom: 12px;
        }
    }

    > .MuiPaper-root {
        max-height: calc(100vh - 96px);
        height: calc(100vh - 96px);
    }

    .dialog-title {
        display: flex;
        justify-content: space-between;
    }

    .json-view-container {
        overflow: auto;
        max-height: calc(100vh - 400px);
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        padding: 8px;
    }
`;

export const HTMLProfileParserCheckNotification: React.FC<{
    data: {
        personId: string;
        issues: string[];
        rawHtmlDataKey: string;
        parsedJSONDataKey: string;
        url: string;
        appVersion: string;
        inferenceId: string;
    };
    userId: string;
    createdAt: number;
    onComplete: () => void;
}> = ({ data, userId, createdAt, onComplete }) => {
    const theme = useTheme();
    const { user } = useSession();
    const { setList } = useSlides();
    const { showPromptInfo } = usePromptInfo();
    const { setAlert } = useModal();
    const [detailsOpen, setDetailsOpen] = React.useState(false);
    const [selectedTab, setSelectedTab] = React.useState(0);
    const [parsedProfile, setParsedProfile] = React.useState<any | null>(null);
    const [loading, setLoading] = React.useState(false);

    const handleOpenDetails = async () => {
        setDetailsOpen(true);
        if (data.parsedJSONDataKey && !parsedProfile) {
            setLoading(true);
            try {
                const response = await downloadFile(data.parsedJSONDataKey);
                const json = await response.json();
                setParsedProfile(json);
            } catch (error) {
                setAlert('Error', 'Failed to load parsed profile: ' + error?.message);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedTab(0);
    };

    const handleShowPersonCard = () => {
        setList([{ personId: data.personId }], { personId: data.personId });
    };

    const handleShowPrompts = () => {
        showPromptInfo({ inferenceId: data.inferenceId });
    };

    const handleTabChange = (_1: React.ChangeEvent<{}>, newValue: number) => {
        setSelectedTab(newValue);
    };

    const handleMarkComplete = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onComplete();
    };

    const actions =
        user.id === userId ? (
            <div className="item-actions">
                <Tooltip title="OK">
                    <IconButton size="small" onClick={handleMarkComplete}>
                        <CheckCircleOutline fontSize="small" />
                    </IconButton>
                </Tooltip>
            </div>
        ) : null;

    const issuesList = data.issues.map((issue, i) => (
        <ListItem disableGutters={true} key={i}>
            <ListItemText primary={issue} />
        </ListItem>
    ));

    const showPersonCardButton = data.personId ? (
        <Button onClick={handleShowPersonCard}>View Person Card</Button>
    ) : null;

    let tabContent;
    switch (selectedTab) {
        case 0:
            // Source HTML tab - always render, RenderRawHTMLSafely handles its own loading
            tabContent = (
                <RenderRawHTMLSafely
                    htmlDataKey={data.rawHtmlDataKey}
                    allowedDomains={['https://*.licdn.com']}
                    preprocessHtml={sanitizeHtmlForIframe}
                    height="calc(100vh - 400px)"
                />
            );
            break;
        case 1:
            // Parsed Profile tab - show loading skeleton while fetching
            if (loading) {
                tabContent = <Skeleton variant="rect" height="300px" />;
            } else if (parsedProfile) {
                tabContent = <PersonProfile profile={parsedProfile} personId={data.personId} />;
            } else {
                tabContent = <div>No parsed profile data available</div>;
            }
            break;
        case 2:
            // Parsed JSON tab - show loading skeleton while fetching
            if (loading) {
                tabContent = <Skeleton variant="rect" height="300px" />;
            } else if (parsedProfile) {
                tabContent = (
                    <div className="json-view-container">
                        <ReactJsonView
                            src={parsedProfile}
                            theme="rjv-default"
                            collapsed={2}
                            displayDataTypes={false}
                            enableClipboard={true}
                            displayObjectSize={true}
                            name={false}
                            sortKeys={true}
                        />
                    </div>
                );
            } else {
                tabContent = <div>No parsed profile data available</div>;
            }
            break;
        default:
            tabContent = null;
    }

    const detailsDialog = (
        <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="xl" fullWidth={true} css={dialogStyles}>
            <DialogTitle>
                <Typography variant="h4" component="div" className="dialog-title">
                    <span>HTML Profile Parsing Issues</span>
                    <span>{data.appVersion}</span>
                </Typography>
            </DialogTitle>
            <DialogContent>
                <List disablePadding={true} dense={true}>
                    {issuesList}
                </List>
                <Tabs value={selectedTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                    <Tab label="Source HTML" />
                    <Tab label="Parsed Profile" />
                    <Tab label="Parsed JSON" />
                </Tabs>
                {tabContent}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleShowPrompts}>View Prompts</Button>
                <Button
                    href={`https://${data.url?.replace(/^https?:\/\//, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Visit LinkedIn Page
                </Button>
                {showPersonCardButton}
                <Button onClick={onComplete}>Clear</Button>
                <Button onClick={handleCloseDetails}>Close</Button>
            </DialogActions>
        </Dialog>
    );

    const primary = <Typography variant="h6">HTML Profile Parsing Issues</Typography>;

    return (
        <>
            <ListItem css={styles(theme)} onClick={handleOpenDetails} button={true}>
                <ListItemAvatar>
                    <Avatar>
                        <BugReport />
                    </Avatar>
                </ListItemAvatar>
                <ListItemText disableTypography={true} primary={primary} />
                <div>
                    <Typography
                        color="textSecondary"
                        noWrap={true}
                        variant="caption"
                        className="caption"
                        component="div"
                    >
                        <span className="left-pad">{notificationTime(createdAt)}</span>
                    </Typography>
                </div>
                {actions}
            </ListItem>
            {detailsDialog}
        </>
    );
};
